import React, { useRef, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const Swapers = ({
  slidesData,
  renderSlide,
  slidesPerView = 1,
  spaceBetween = 0,
  loop = false,
  autoplay = false,
  navigation = false,
  pagination = false,
  speed = 1000,
  breakpoints = {},
  onSwiper,
  onSlideChange,
  equalizeHeights = false,
}) => {
  const swiperRef = useRef(null);
  const isUpdatingHeight = useRef(false);
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const activeModules = [Navigation, Pagination];
if (autoplay) activeModules.push(Autoplay);

  useEffect(() => {
    if (equalizeHeights) {
      const equalize = () => {
        if (swiperRef.current && swiperRef.current.swiper && !isUpdatingHeight.current) {
          isUpdatingHeight.current = true;
          const swiperInstance = swiperRef.current.swiper;
          const slides = swiperInstance.slides;
          let maxHeight = 0;

          slides.forEach((slide) => {
            slide.style.height = 'auto';
            const slideHeight = slide.offsetHeight;
            if (slideHeight > maxHeight) {
              maxHeight = slideHeight;
            }
          });

          slides.forEach((slide) => {
            slide.style.height = `${maxHeight}px`;
          });

          requestAnimationFrame(() => {
            swiperInstance.update();
            isUpdatingHeight.current = false;
          });
        }
      };

      const handleResize = () => {
        equalize();
      };

      const handleInit = () => {
        equalize();
      };

      // Initial call and event listeners
      equalize();
      window.addEventListener('resize', handleResize);
      const swiperInstance = swiperRef.current?.swiper;
      if (swiperInstance) {
        swiperInstance.on('init', handleInit);
        // You might want to re-equalize on slide change if content varies significantly
        // swiperInstance.on('slideChange', equalize);
      }

      return () => {
        window.removeEventListener('resize', handleResize);
        if (swiperInstance) {
          swiperInstance.off('init', handleInit);
          // swiperInstance.off('slideChange', equalize);
        }
      };
    }
  }, [equalizeHeights, slidesData]);

  const pauseAutoPlay = () => {
    const swiper = swiperRef.current?.swiper;
    if (swiper?.autoplay?.running) {
      swiper.autoplay.stop();
    }
  };
  
  const resumeAutoPlay = () => {
    const swiper = swiperRef.current?.swiper;
    if (swiper?.autoplay && !swiper.autoplay.running) {
      swiper.autoplay.start();
    }
  };

  useEffect(() => {
    const swiperInstance = swiperRef.current?.swiper;

    if (swiperInstance && prevRef.current && nextRef.current) {
      swiperInstance.params.navigation.prevEl = prevRef.current;
      swiperInstance.params.navigation.nextEl = nextRef.current;

      // Destroy and re-init navigation to apply new buttons
      swiperInstance.navigation.destroy();
      swiperInstance.navigation.init();
      swiperInstance.navigation.update();
    }
  }, [slidesData]);

  useEffect(() => {
    const swiperInstance = swiperRef.current?.swiper;
    if (swiperInstance) {
      setTimeout(() => {
        swiperInstance.update();
      }, 100); // Small delay to allow DOM layout to settle
    }
  }, []);

  return (
    <div className="relative" onMouseEnter={pauseAutoPlay}
    onMouseLeave={resumeAutoPlay}>
      <Swiper
        ref={swiperRef}
        modules={activeModules}
        slidesPerView={slidesPerView}
        spaceBetween={spaceBetween}
        observer={true}
        observeParents={true}
        loop={loop}
        autoplay={autoplay ? { delay: 3000 } : false}
        navigation={{
          prevEl: prevRef.current,
          nextEl: nextRef.current,
        }}
        pagination={pagination}
        speed={speed}
        breakpoints={breakpoints}
        onSwiper={onSwiper}
        onSlideChange={onSlideChange}
        autoHeight={!equalizeHeights} // Only use Swiper's autoHeight if we're not manually equalizing
      >
        {slidesData &&
          slidesData.map((slide, index) => (
            <SwiperSlide key={index}>
              {renderSlide(slide)}
            </SwiperSlide>
          ))}
      </Swiper>
      {/* Custom Nav Buttons */}
      <button
        className="zoom-carousel-nav prev-button btn  btn-outline-light rounded-circle shadow"
        ref={prevRef}
      >
       <i className="bi bi-chevron-left"></i>
      </button>
      <button
        className="zoom-carousel-nav next-button btn  btn-outline-light rounded-circle shadow"
        ref={nextRef}
      >
        <i className="bi bi-chevron-right"></i>
      </button>
    </div>
  );
};

export default Swapers;